import type { RawMqttMessage } from '@/lib/mqtt/types';

/**
 * Clipboard text for a selected message. Mirrors the mockup's `dsCopyMessage`
 * (pretty-printed payload, independent of the active JSON/Raw/History tab).
 *
 * `emitRaw` stores the un-parseable payload verbatim in `payload` when the body
 * isn't JSON, so stringifying it there would hand back an escaped, quoted
 * string. Copy `raw` in that case instead.
 */
export function buildCopyText(msg: RawMqttMessage | null): string {
  if (!msg) return '';
  if (typeof msg.payload === 'string') return msg.raw;
  return JSON.stringify(msg.payload, null, 2);
}

/**
 * Writes to the clipboard, falling back to a hidden-textarea `execCommand`.
 *
 * The Clipboard API can reject even where it exists — denied permission,
 * non-secure context, no user gesture — so a bare `writeText` would leave the
 * click looking like a no-op. Returns whether the text was copied.
 */
export async function writeClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to the legacy path
    }
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    // Deprecated, but only reachable when the Clipboard API is absent or has
    // rejected — there is no supported replacement for that path.
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}
